// pages/BlogPostEditor.jsx  (or components/VlogEditor.jsx)
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config/config'; // assuming you have this like in FlashBarAdmin
import RichTextEditor from '../components/RichTextEditor';
import { useParams, useNavigate } from 'react-router-dom'; // if using react-router

const BlogPostEditor = () => {
  const { id } = useParams(); // if editing existing post (e.g. /admin/vlog/edit/:id)
  const navigate = useNavigate();
  const isNewPost = !id;

  const token = localStorage.getItem('token');

  // Form fields
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('General');
  const [featuredImage, setFeaturedImage] = useState('');
  const [status, setStatus] = useState('draft'); // draft | published
  const [publishDate, setPublishDate] = useState('');
  const [loading, setLoading] = useState(isNewPost ? false : true);
  const [saving, setSaving] = useState(false);

  // Fetch existing vlog/post data
  useEffect(() => {
    if (isNewPost) return;

    const fetchVlog = async () => {
      try {
        setLoading(true);
        await axios.get(config.API_URL + '/sanctum/csrf-cookie', { withCredentials: true });

        const response = await axios.get(
          `${config.API_URL}/api/pullVlogSettings/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          }
        );

        const post = response.data.vlog || response.data.post || {};

        setTitle(post.title || '');
        setSlug(post.slug || '');
        setExcerpt(post.excerpt || '');
        setContent(post.content || '');
        setCategory(post.category || 'General');
        setFeaturedImage(post.featuredImage || '');
        setStatus(post.status || 'draft');
        setPublishDate(post.publishDate ? new Date(post.publishDate).toISOString().slice(0, 16) : '');

      } catch (error) {
        console.error('Failed to load vlog post:', error);
        alert('Could not load post. Using blank form.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchVlog();
    }
  }, [id, token, isNewPost]);

  // Auto-generate slug from title
  useEffect(() => {
    if (title && isNewPost) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setSlug(generatedSlug);
    }
  }, [title, isNewPost]);

  const handleSave = async (htmlContent) => {
    if (!title.trim()) {
      alert('Title is required!');
      return;
    }

    setSaving(true);

    try {
      await axios.get(config.API_URL + '/sanctum/csrf-cookie', { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const payload = {
        title,
        slug: slug || title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, ''),
        excerpt,
        content: htmlContent,
        category,
        featuredImage,
        status,
        publishDate: publishDate ? new Date(publishDate).toISOString() : null,
      };

      let response;

      if (isNewPost) {
        response = await axios.post(
          `${config.API_URL}/api/saveVlog`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
      } else {
        response = await axios.put(
          `${config.API_URL}/api/updateVlog/${id}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
      }

      if (response.data.status === 1 || response.status === 200 || response.status === 201) {
        alert(isNewPost ? 'Article created successfully!' : 'Article updated successfully!');
        if (isNewPost && response.data.id) {
          navigate(`/admin/vlog/edit/${response.data.id}`);
        }
      } else {
        alert('Save failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save article. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading article...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="bg-white border-b px-6 py-4 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            {isNewPost ? 'Create New Article' : 'Edit Article'}
          </h1>
          <div className="flex items-center gap-4">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-4 py-2 border rounded-md text-sm"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
            <button
              onClick={() => handleSave(content)}
              disabled={saving}
              className={`px-6 py-2.5 rounded-lg font-medium text-white ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              } transition-colors flex items-center gap-2`}
            >
              {saving ? 'Saving...' : 'Save Article'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pt-8">
        {/* Title */}
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter post title..."
          className="w-full px-0 py-4 text-4xl font-bold border-none focus:outline-none bg-transparent placeholder-gray-400"
        />

        {/* Slug */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug</label>
          <div className="flex items-center">
            <span className="bg-gray-100 px-3 py-2 rounded-l-md text-gray-600">/blog/</span>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="my-awesome-article"
            />
          </div>
        </div>

        {/* Excerpt */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt / Meta Description</label>
          <textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="A short summary of the article (used in previews and SEO)..."
          />
        </div>

        {/* Featured Image */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-1">Featured Image URL</label>
          <input
            type="url"
            value={featuredImage}
            onChange={(e) => setFeaturedImage(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="https://example.com/images/my-post-cover.jpg"
          />
          {featuredImage && (
            <div className="mt-3">
              <img
                src={featuredImage}
                alt="Featured preview"
                className="max-h-64 object-cover rounded-lg border"
                onError={(e) => (e.target.style.display = 'none')}
              />
            </div>
          )}
        </div>

        {/* Rich Content Editor */}
        <div className="mb-12">
          <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
          <RichTextEditor
            initialContent={content}
            onSave={handleSave}
          />
        </div>

        {/* Extra metadata */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              placeholder="Design, Marketing, Development..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Publish Date</label>
            <input
              type="datetime-local"
              value={publishDate}
              onChange={(e) => setPublishDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostEditor;